import { ReactNode, useCallback, useId, useRef, useState } from "react";
import Dialog from "@mui/material/Dialog";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import { Button } from "../Button";
import { IconButton } from "../IconButton";
import { Input } from "../Field";
import { useNextT } from "../../i18n";
import { usePhone } from "../usePhone";
import { ToastHost, useToastAvoid } from "../Toast";
import s from "./Modal.module.scss";

/**
 * `onExited` runs once the close animation is over (the place to fire a toast: confirm -> close -> toast).
 * `closeLabel` names the ✕ when "Close" is not the whole truth (e.g. "Close without saving"); `describedBy` points at an intro line.
 * `headDivider={false}`: no line under the title, for a body that continues the heading (the contact page's status line).
 * `phoneFullScreen`: on a phone the dialog fills the screen (a long form, e.g. the visit report) instead of a bottom sheet.
 */
/**
 * True while the element's content is taller than the element (it scrolls). Such a body becomes a focusable, named region:
 * keyboard users can then scroll a body that holds no control of its own (WCAG 2.1.1, axe scrollable-region-focusable).
 */
const useScrolls = () => {
  const [scrolls, setScrolls] = useState(false);
  const observer = useRef<ResizeObserver | null>(null);
  // Measured as the body mounts (no extra render when it fits), then again whenever it or its content resizes.
  const ref = useCallback((el: HTMLElement | null) => {
    observer.current?.disconnect();
    observer.current = null;
    if (!el) return;
    const check = () => setScrolls(el.scrollHeight > el.clientHeight + 1);
    check();
    if (typeof ResizeObserver === "undefined") return;
    observer.current = new ResizeObserver(check);
    observer.current.observe(el);
    Array.from(el.children).forEach((c) => observer.current?.observe(c));
  }, []);
  return { ref, scrolls };
};

export const Modal = ({ open, title, onClose, children, actions, size = "md", headerAction, onExited, closeLabel, describedBy, headDivider = true, phoneFullScreen = false }:
  { open: boolean; title: ReactNode; onClose: () => void; children: ReactNode; actions?: ReactNode; size?: "sm" | "md" | "lg"; headerAction?: ReactNode; onExited?: () => void; closeLabel?: string; describedBy?: string; headDivider?: boolean; phoneFullScreen?: boolean }) => {
  const { t } = useNextT();
  const id = useId();
  const phone = usePhone();
  const body = useScrolls();
  const avoid = useToastAvoid();
  const full = phone && phoneFullScreen;
  const sheet = phone && !full;
  return (
    <Dialog open={open} onClose={onClose} aria-labelledby={id} aria-describedby={describedBy} fullWidth maxWidth={size} fullScreen={full}
      className={`cy-next ${sheet ? s.sheet : full ? s.full : ""}`} PaperProps={{ className: s.paper }} TransitionProps={onExited ? { onExited } : undefined}>
      {sheet && <div aria-hidden="true" data-testid="sheet-handle" className={s.handle} />}
      <header className={headDivider ? s.head : `${s.head} ${s.headFlush}`}>
        <h2 id={id} className={s.title}>{title}</h2>
        <div className={s.headEnd}>
          {headerAction}
          <IconButton label={closeLabel ?? t("ui.close")} icon={<CloseRoundedIcon />} onClick={onClose} />
        </div>
      </header>
      <div ref={body.ref} className={s.body} {...(body.scrolls ? { tabIndex: 0, role: "region", "aria-labelledby": id } : {})}>{children}</div>
      {actions && <footer ref={avoid} className={s.actions}>{actions}</footer>}
      <ToastHost active={open} />
    </Dialog>
  );
};

/** The sentence with the user-entered name inside <bdi>, so Hebrew text cannot reorder it. */
const typeToConfirm = (sentence: string, name: string): ReactNode => {
  const [before, after = ""] = sentence.split("\u0000");
  return <>{before}<bdi>{name}</bdi>{after}</>;
};

/** Bidi and zero-width marks (U+200B–200F, U+202A–202E, U+2066–2069, U+FEFF): invisible, so never part of a typed name. */
const INVISIBLE_MARKS = /[\u200B-\u200F\u202A-\u202E\u2066-\u2069\uFEFF]/g;

/**
 * A typed name as the server compares it (2F R20): invisible marks stripped, NFC (one composition for the same
 * letters and points), every run of spaces made one, trimmed, lower case.
 */
export const normalizeTypedName = (v: string): string =>
  v.replace(INVISIBLE_MARKS, "").normalize("NFC").replace(/\s+/g, " ").trim().toLowerCase();

/**
 * `confirmName`: the confirm button stays off until that name is typed (`normalizeTypedName` on both sides, so
 * Hebrew typed with a trailing space, a different composition or a stray direction mark still matches); `nameHint` is
 * a line under the field, tied to it. `nameLabel` names the field in the caller's own words (the name in <bdi>), instead of
 * the kit's "Type {name} to confirm"; `nameError` marks the field invalid and is read with it (e.g. the name was refused);
 * `onTyped` hears every change of the typed name (e.g. to send it, or to clear that error).
 * While a typed name or number is still missing the button stays readable (grey fill, muted words, board 51) instead of fading.
 * `confirmDisabled` keeps the confirm button off (e.g. while a count loads) without blocking Cancel; `onExited` as in Modal.
 * A danger dialog shows a red tile beside the title: `icon` (e.g. a trash can for deletes), else a warning sign.
 * The safe button is focused on open and always shows its ring then, even when the dialog was opened with a pointer.
 * `onDismiss`: Escape / a tap outside, when that is not the cancel button's answer (e.g. two real choices: dismissing
 * goes back without either); defaults to `onCancel`. `focusConfirm`: the confirm button is the default answer and takes
 * the focus (and the ring) instead of the cancel button — only for a choice where nothing is lost (e.g. with `danger` off).
 * `phoneSheet` (2F-b, board 51): on a phone the confirm is a bottom sheet with the brand handle, without the tile, its two
 * buttons side by side. Opt-in: without it a phone shows the usual dialog, as every confirm did before.
 */
export const ConfirmDialog = ({ open, title, body, confirmLabel, cancelLabel, danger = true, icon, confirmName, nameHint, nameLabel, nameError, onTyped, confirmNumber, numberLabel, numberHint, busy, confirmDisabled, focusConfirm, phoneSheet = false, onConfirm, onCancel, onDismiss, onExited }:
  { open: boolean; title: ReactNode; body: ReactNode; confirmLabel: string; cancelLabel?: string; danger?: boolean; icon?: ReactNode; confirmName?: string; nameHint?: string; nameLabel?: ReactNode; nameError?: string; onTyped?: (value: string) => void; confirmNumber?: number; numberLabel?: (n: number) => string; numberHint?: string; busy?: boolean; confirmDisabled?: boolean; focusConfirm?: boolean; phoneSheet?: boolean; onConfirm: () => void; onCancel: () => void; onDismiss?: () => void; onExited?: () => void }) => {
  const { t } = useNextT();
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const [typed, setTyped] = useState("");
  const safeRef = useRef<HTMLButtonElement>(null);
  const avoid = useToastAvoid();
  const phone = usePhone();
  const sheet = phoneSheet && phone;
  // A reopened (or retargeted) dialog must never arrive pre-filled.
  // Reset while rendering (React's "adjust state on prop change" pattern), not in an effect, so no stale frame shows the old text.
  const target = `${confirmName ?? ""}|${confirmNumber ?? ""}`;
  const [seenTarget, setSeenTarget] = useState(target);
  if (seenTarget !== target) { setSeenTarget(target); setTyped(""); }
  if (!open && typed !== "") setTyped("");
  const expected = confirmName === undefined ? "" : normalizeTypedName(confirmName);
  // A name made only of invisible marks can never be typed: the button stays off rather than matching "".
  const nameOk = !confirmName || (expected !== "" && normalizeTypedName(typed) === expected);
  const numberOk = confirmNumber === undefined || typed.trim() === String(confirmNumber);
  const waiting = !nameOk || !numberOk;
  return (
    <Dialog open={open} onClose={busy ? undefined : onDismiss ?? onCancel} aria-labelledby={id} maxWidth="xs" fullWidth
      className={sheet ? `cy-next ${s.sheet}` : "cy-next"} PaperProps={{ className: s.paper }} TransitionProps={{ onEntered: () => safeRef.current?.focus(), onExited }}
      disableAutoFocus>
      {sheet && <div aria-hidden="true" data-testid="sheet-handle" className={s.handle} />}
      <div className={s.confirmBody}>
        <div className={s.confirmHead}>
          {danger && !sheet && <span aria-hidden className={s.dangerTile}>{icon ?? <ErrorOutlineRoundedIcon />}</span>}
          <h2 id={id} className={s.title}>{title}</h2>
        </div>
        <div className={s.text}>{body}</div>
        {confirmName && (
          <label className={s.typeName}>
            <span>{nameLabel ?? typeToConfirm(t("ui.type_to_confirm", { name: "\u0000" }), confirmName)}</span>
            <Input value={typed} onChange={(e) => { setTyped(e.target.value); onTyped?.(e.target.value); }} autoComplete="off" invalid={!!nameError}
              aria-describedby={[nameHint ? hintId : "", nameError ? errorId : ""].join(" ").trim() || undefined} />
          </label>
        )}
        {confirmName && nameHint && <span id={hintId} className={s.typeHint}>{nameHint}</span>}
        {confirmName && nameError && <span id={errorId} role="alert" className={s.typeError}>{nameError}</span>}
        {confirmNumber !== undefined && (
          <label className={s.typeName}>
            <span>{numberLabel ? numberLabel(confirmNumber) : String(confirmNumber)}</span>
            <Input value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" inputMode="numeric" dir="ltr" aria-describedby={numberHint ? hintId : undefined} />
          </label>
        )}
        {confirmNumber !== undefined && numberHint && <span id={hintId} className={s.typeHint}>{numberHint}</span>}
      </div>
      <footer ref={avoid} className={s.actions}>
        <Button ref={focusConfirm ? undefined : safeRef} autoFocus={!focusConfirm} className={focusConfirm ? undefined : s.safe} onClick={onCancel} disabled={busy}>{cancelLabel ?? t("ui.cancel")}</Button>
        <Button ref={focusConfirm ? safeRef : undefined} autoFocus={focusConfirm} className={[focusConfirm ? s.safe : "", waiting ? s.waiting : ""].join(" ").trim() || undefined} variant={danger ? "danger" : "primary"}
          onClick={onConfirm} disabled={!nameOk || !numberOk || confirmDisabled} loading={busy}>{confirmLabel}</Button>
      </footer>
      <ToastHost active={open} />
    </Dialog>
  );
};
